import { useEffect, useState, useRef } from 'react';
import { Button, Col, Container, Form, Row, Stack, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Typeahead } from 'react-bootstrap-typeahead';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { getFileInfo, updateScriptPreviews, getPreviewCount, setScriptTags } from '../utils/Database';
import { getTagFolders } from '../utils/Views';
import { getPreviewSignedURL } from '../utils/Storage';
import { showToast } from '../utils/Toasts';
import { downloadFile, checkDownloadLimit, clearEditing } from "../utils/Database";
import { formatDateTime } from '../utils/Dates';
import { getIndexInfo } from '../utils/Indexing';
import { getUserInfo, updateUserStrikes, blacklistUser } from '../utils/UserUtil';
import { getFavorite, saveFavorite, removeFavorite } from '../utils/Favorites';
import { usePageVisibility } from '../utils/PageVisibility';

import { LoadingRing } from '../components/LoadingRing';
import { LoadingText } from '../components/LoadingText';
import PdfViewer from '../components/PdfViewer';
import AltViewer from '../components/AltViewer';
import { ConfirmationModal, DeleteConfirmationModal, RestoreConfirmationModal, RemoveDuplicatesModal } from '../components/Confirmation';

import MatrixBackground from "../components/MatrixBackground";

import useUserStore from '../stores/User';
import { getDefaultBenefits, userHasElevatedAccess, userHasBenefit } from '../utils/Benefits';

const DAILY_PREVIEW_LIMIT = parseInt(
    process.env.REACT_APP_DAILY_SCRIPTS_PREVIEW_LIMIT,
    10
) || 25; // Default to 25 if not set or invalid

const BLACKLIST_RATE_SECONDS = parseInt(
    process.env.REACT_APP_BLACKLIST_RATE_SECONDS,
    10
) || 2; // Default to 2 if not set or invalid

const HIDE_PREVIEW = process.env.REACT_APP_HIDE_PREVIEW === 'true'; // Default to false if not set

function ScriptView() {
    const isPageVisible = usePageVisibility();
    const isExtraLarge = useMediaQuery({ query: '(min-width: 1200px)' });
    const userData = useUserStore((state) => state.data);
    const userDataRef = useRef(userData);
    useEffect(() => { userDataRef.current = userData; }, [userData]);
    const isLoading = useUserStore((state) => state.loading);
    const { file_id } = useParams<{ file_id: string }>();
    const [fileInfo, setFileInfo] = useState<any>(null);
    const [fileType, setFileType] = useState<string>('pdf');
    const [recordDeleted, setRecordDeleted] = useState<boolean>(false);
    const [indexedBy, setIndexedBy] = useState<any>(null);
    const [modifiedBy, setModifiedBy] = useState<any>(null);
    const [userCanEdit, setUserCanEdit] = useState<boolean>(userData?.isApprover || userData?.isCurator || false);
    const [checkedOut, setCheckedOut] = useState<boolean>(false);
    const [editorEmail, setEditorEmail] = useState<React.ReactNode>(null);
    const [downloadingScriptId, setDownloadingScriptId] = useState<string | null>(null);
    const [unlimitedDownloads, setUnlimitedDownloads] = useState(userData?.isApprover || false);
    const [url, setUrl] = useState<string>('');
    const [isTV, setIsTV] = useState<boolean>(false);
    const [isFavorite, setIsFavorite] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showRemoveDuplicatesModal, setShowRemoveDuplicatesModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);

    const requestedSignedURLRef = useRef(false);
    const loggedPreviewRef = useRef(false);
    const [showPreview, setShowPreview] = useState(!HIDE_PREVIEW);
    const [totalPreviewCount, setTotalPreviewCount] = useState(0);
    const [recentPreviewCount, setRecentPreviewCount] = useState(0);
    // Preview limits skipped for admins / curators / anyone with paid-tier features
    const [noPreviewLimit, setNoPreviewLimit] = useState(userData?.isApprover || userHasElevatedAccess(userData) || userData?.isCurator || false);
    const [isBlacklisted, setIsBlacklisted] = useState(userData?.isBlacklisted || false);
    const [tooManyPreviews, setTooManyPreviews] = useState(false);

    const [showMatrixEasterEgg, setShowMatrixEasterEgg] = useState(false);

    const [tagFolders, setTagFolders] = useState<{ id: number; name: string; tags: string[] }[]>([]);
    const [tagCategory, setTagCategory] = useState('');
    const [tagIndustry, setTagIndustry] = useState('');
    const [tagYear, setTagYear] = useState('');
    const [tagGenre, setTagGenre] = useState<string[]>([]);
    const [tagIsPilot, setTagIsPilot] = useState(false);
    const canEditTags = !!(userData?.isApprover || userData?.isCurator || (userData as any)?.isTagger);

    // Star tooltip: only nudge non-patrons who lack the favoriting feature
    const renderTooltip = (props) => (
        userHasBenefit(userData, "scriptFavoriting") ? (
            <></>
        ) : (
            <Tooltip id="button-tooltip" {...props}>
                Become a <Link to="https://patreon.com/scripthive" target="_blank">Gold Level</Link> member on Patreon to access Favorites and other exclusive features!
            </Tooltip>
        )
    );

    /**
     * Check if the current navigation is a browser pre-fetch
     * Returns false if the page is being prerendered/prefetched
     */
    const isNotAPrefetch = (): boolean => {
        // If the page is being prerendered/prefetched, skip logging the preview
        if ('prerendering' in document && document.prerendering) {
            return false;
        }

        return true;
    };

    useEffect(() => {
        // Listen for when a prefetched page becomes active
        const handlePrerenderingChange = () => {
            if ('prerendering' in document && !document.prerendering) {
                // Page transitioned from prerendering to active
                // Reset the ref so the preview can be logged when fetchData runs
                loggedPreviewRef.current = false;
                // Re-run fetchData to log the preview now that page is active
                if (fileInfo && !isLoading) {
                    fetchData();
                }
            }
        };

        // Listen for visibility change as a fallback (some browsers use this instead)
        const handleVisibilityChange = () => {
            if ('prerendering' in document && document.prerendering === false && !loggedPreviewRef.current && fileInfo && !isLoading) {
                // Page became visible after being prerendered
                fetchData();
            }
        };

        document.addEventListener('prerenderingchange', handlePrerenderingChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('prerenderingchange', handlePrerenderingChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileInfo, isLoading]);

    useEffect(() => {
        if (!userData) return;
        
        setUnlimitedDownloads(userData?.isApprover || false);
        setNoPreviewLimit(userData?.isApprover || userHasElevatedAccess(userData) || userData?.isCurator || false);
        setIsBlacklisted(userData?.isBlacklisted || false);
    }, [userData]);

    const fetchData = async () => {
        const file = await getFileInfo(file_id);

        if (file) {
            setRecordDeleted(!!file.record_deleted);

            if (recordDeleted && !userDataRef.current?.isApprover) {
                showToast("error", "Sorry, that's not a valid link.");
                navigate("/database"); // go to the database main page
                return;
            }

            if (!loggedPreviewRef.current && !recordDeleted && isNotAPrefetch()) {
                const previewResult = await updateScriptPreviews(file["file_id"], userDataRef.current?.user_id);
                if (previewResult) {
                    loggedPreviewRef.current = true;
                }
            }

            const previewCount = await getPreviewCount(userDataRef.current?.user_id);
            setTotalPreviewCount(previewCount.todayCount);
            setRecentPreviewCount(previewCount.recentCount);

            if (file['Script Title'].indexOf('Matrix') >= 0) {
                setShowMatrixEasterEgg(true);
            }
            if (file["user_editing_id"] !== null && file["user_editing_id"] !== userDataRef.current?.user_id) {
                const ts = file["user_editing_timestamp"];
                if (ts) {
                    const editingTime = new Date(ts);
                    if (!isNaN(editingTime.getTime()) && Date.now() - editingTime.getTime() > 60 * 60 * 1000) {
                        // Editing has expired (1 hour)
                        clearEditing(file_id!, file["user_editing_id"]);
                    } else {
                        const currentEditor = await getUserInfo(file["user_editing_id"]);

                        if (currentEditor && (currentEditor.isApprover || currentEditor.isIndexer)) {
                            setEditorEmail(<a href={`mailto:${currentEditor.email}`}>{currentEditor.email}</a>);
                            setCheckedOut(true);
                        } else {
                            clearEditing(file_id!, file["user_editing_id"]);
                        }
                    }
                }
            }

            if (file["Type"] !== "application/pdf" && file["Type"] !== "PDF" && file["Type"] !== null) {
                setFileType('alt');
            }

            if ((file.tags || []).some((t: string) => t.toLowerCase().includes("television"))) {
                setIsTV(true);
            }

            if (file["indexing_id"] !== null) {
                const indexInfo = await getIndexInfo(file["indexing_id"]);
                if (indexInfo.index_user_id !== null) {
                    setUserCanEdit(userDataRef.current?.user_id === indexInfo.index_user_id || userDataRef.current?.isApprover || userDataRef.current?.isCurator || false);

                    const indexUser = await getUserInfo(indexInfo.index_user_id);
                    if (indexUser) {
                        setIndexedBy(<a href={`mailto:${indexUser.email}`} target="_blank" rel="noreferrer">{indexUser.email}</a>);
                    } else {
                        setIndexedBy(<span>An Unknown User</span>);
                    }
                }
            } else {
                setUserCanEdit(
                    userDataRef.current?.isApprover ||
                    userDataRef.current?.isCurator ||
                    file["Writer(s)"] === null ||
                    file.modified_by_user === userDataRef.current?.user_id ||
                    false
                );
            }

            if (file["modified_by_user"] !== null) {
                const modified = await getUserInfo(file["modified_by_user"]);
                if (modified) {
                    setModifiedBy(<a href={`mailto:${modified.email}`} target="_blank" rel="noreferrer">{modified.email}</a>);
                } else {
                    setModifiedBy(<span>An Unknown User</span>);
                }
            }

            if (userHasBenefit(userDataRef.current, "scriptFavoriting")) {
                const favorite = await getFavorite(file["file_id"], userDataRef.current?.user_id);
                if (favorite) {
                    setIsFavorite(true);
                }
            }

            setFileInfo(file);
        } else {
            showToast("error", "Sorry, that's not a valid link.");
            navigate("/database"); // go to the database main page
        }
    };

    useEffect(() => {
        if (file_id && !isLoading) {
            fetchData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file_id, isLoading]);

    useEffect(() => {
        if (!canEditTags) return;
        getTagFolders().then(setTagFolders);
    }, [canEditTags]);

    useEffect(() => {
        if (!fileInfo || tagFolders.length === 0) return;
        const fileTags: string[] = fileInfo.tags || [];
        const catFolder   = tagFolders.find(f => f.name === 'Category');
        const indFolder   = tagFolders.find(f => f.name === 'Industry List');
        const yrFolder    = tagFolders.find(f => f.name === 'Year');
        const genreFolder = tagFolders.find(f => f.name === 'Genre');
        setTagCategory(fileTags.find(t => catFolder?.tags.includes(t)) || '');
        setTagIndustry(fileTags.find(t => indFolder?.tags.includes(t)) || '');
        setTagYear(fileTags.find(t => yrFolder?.tags.includes(t)) || '');
        setTagGenre(fileTags.filter(t => genreFolder?.tags.includes(t)));
        setTagIsPilot(fileTags.includes('Pilot'));
    }, [fileInfo, tagFolders]);

    useEffect(() => {
        // Check the rate of previews per minute and blacklist the user if they're exceeding the threshold
        const shouldBlacklist = (60 / recentPreviewCount) < BLACKLIST_RATE_SECONDS;

        if ((shouldBlacklist) && !noPreviewLimit && !isBlacklisted) {
            if (!userData?.isBlacklisted) {
                blacklistUser(userData?.user_id);
                showToast('error', 'Your account is currently restricted for exceeding our script viewing rate limit. Please contact support.', 10000);
            }

            // Update local state to reflect blacklisting immediately
            setIsBlacklisted(true);
            setShowPreview(false);

            // Add a strike to the user's account
            updateUserStrikes(userData?.user_id, userData?.email, `Exceeded Rate Limit: ${recentPreviewCount} previews in the last minute.`, "Temporary");
        }
        
        if ((totalPreviewCount > DAILY_PREVIEW_LIMIT && !noPreviewLimit)) {
            setShowPreview(false);
            setTooManyPreviews(true);
        }
    }, [totalPreviewCount, recentPreviewCount, noPreviewLimit, isBlacklisted, userData]);

    useEffect(() => {
        // Navigating between scripts in-app reuses this component instance (no remount), so
        // the previous file's signed URL and fetch guard must be reset for the new file_id.
        requestedSignedURLRef.current = false;
        setUrl('');
    }, [file_id]);

    useEffect(() => {
        // Clear the URL if previews were turned off or user got blacklisted while viewing, then return early
        if (!showPreview || isBlacklisted) {
            if (url !== '') {
                setUrl('');
            }
            return;
        }

        // If we already have a URL, don't fetch a new one
        if (url !== '') {
            return;
        }

        const fetchPreview = async () => {
            if (requestedSignedURLRef.current) return;
            requestedSignedURLRef.current = true;
            
            const fileUrl = showPreview && !isBlacklisted ?
                await getPreviewSignedURL(fileInfo["Title"], fileInfo["file_id"], userDataRef.current?.user_id) : null;

            if (fileUrl) {
                setUrl(fileUrl as string);
            } else {
                setShowPreview(false);
            }
        };

        // If we have file info and the page is visible, fetch a PDF preview URL
        if (fileInfo && isPageVisible) {
            fetchPreview();
        }

    }, [isPageVisible, fileInfo, url, showPreview, isBlacklisted]);

    // Shared by the Download button and the preview toolbar so both go through
    // the same daily-limit check and server-side download record.
    const handleDownload = async () => {
        if (downloadingScriptId !== null) return; // Prevent multiple downloads at once
        setDownloadingScriptId(fileInfo["file_id"]);

        let remaining = 0;
        let downloadLimit = 0;

        if (!unlimitedDownloads) {
            const allowance = await checkDownloadLimit(userData?.user_id);
            if (!allowance) {
                setDownloadingScriptId(null);
                return;
            }
            remaining = allowance.remaining;
            downloadLimit = allowance.limit;
        }

        if (remaining > 0 || unlimitedDownloads) {
            const downloadSuccess = await downloadFile(fileInfo["file_id"], userData?.user_id);
            setDownloadingScriptId(null);
            if (downloadSuccess && !unlimitedDownloads) {
                showToast("info", `You have ${(remaining - 1) === 0 ? "no more" : (remaining - 1)} download(s) remaining today.${downloadLimit > (getDefaultBenefits().downloadsPerDay as number) ? '' : ' Gold Level Patreons have a higher limit!'}`);
            }
        } else {
            showToast("info", `Sorry, but you've reached the daily download limit of ${downloadLimit} per day. Please try again tomorrow.${downloadLimit > (getDefaultBenefits().downloadsPerDay as number) ? '' : ' Gold Level Patreons have a higher limit!'}`);
            setDownloadingScriptId(null);
        }
    };

    return (
        <div className="hive-container d-flex">
            <MatrixBackground show={showMatrixEasterEgg} />
            <div className="d-flex justify-content-between w-100">
                <div className="mx-sm-5 w-100 pb-1 pb-md-4">
                    {fileInfo && !isLoading ? (
                        <Container fluid className="p-3" style={{ height: 'calc(100vh - 120px)' }}>
                            <Row className={`g-3 ${isExtraLarge ? "h-100" : ""} min-zero`} xs={2}>
                                <Col xl={6} xs={12} className="pe-lg-3 d-flex flex-column overflow-hidden h-100 min-zero order-last order-xl-0 mb-4 mb-lg-0">
                                    <Row>
                                        <Col xs={12}>
                                            <h1
                                                className="fs-5 fw-bold text-break"
                                                title={fileInfo["Script Title"]}
                                            >
                                                {recordDeleted && (
                                                    <span className="text-danger me-1">[Deleted]</span>
                                                )}
                                                {!recordDeleted && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        delay={{ show: 250, hide: 2000 }}
                                                        overlay={renderTooltip}
                                                    >
                                                        <span className="d-inline-block">
                                                            <button
                                                                className="icon-only-button p-0 me-2"
                                                                disabled={!userHasBenefit(userData, "scriptFavoriting")}
                                                                onClick={() => {
                                                                    if (isFavorite) {
                                                                        removeFavorite(fileInfo["file_id"], userData?.user_id).then((success) => {
                                                                            if (success) {
                                                                                setIsFavorite(false);
                                                                                showToast("info", `Removed ${fileInfo["Script Title"]} from Favorites`, 1000);
                                                                            } else {
                                                                                showToast("error", `Unable to remove ${fileInfo["Script Title"]} from Favorites`, 1000);
                                                                            }
                                                                        });
                                                                    } else {
                                                                        saveFavorite(fileInfo["file_id"], userData?.user_id).then((success) => {
                                                                            if (success) {
                                                                                setIsFavorite(true);
                                                                                showToast("success", `Added ${fileInfo["Script Title"]} to Favorites`, 1000);
                                                                            } else {
                                                                                showToast("error", `Unable to add ${fileInfo["Script Title"]} to Favorites`, 1000);
                                                                            }
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                <i className={`bi ${userHasBenefit(userData, "scriptFavoriting") ? "bi-star-fill" : "bi-star"}${isFavorite ? " selected" : ""}`} aria-label="Favorite" title="Favorite"></i>
                                                            </button>
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                                {fileInfo["Script Title"]}
                                            </h1>
                                            <h2 className="fs-6 mb-2 text-break">{`Written by ${fileInfo["Writer(s)"] || "Unlisted"}`}</h2>
                                        </Col>
                                    </Row>
                                    <Row xs={1} lg={2}>
                                        <Col md={12} xs={12} className="min-zero">
                                            {!canEditTags && fileInfo.tags && fileInfo.tags.length > 0 && (
                                                <>
                                                    <h3 className="fs-6 fw-bold mt-2 mb-1">Tags</h3>
                                                    <div className="d-flex flex-wrap gap-1 mb-2">
                                                        {fileInfo.tags.map((tag: string) => (
                                                            <span key={tag} className="badge bg-secondary">{tag}</span>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {canEditTags && (
                                                <>
                                                    <h3 className="fs-6 fw-bold mt-2 mb-2">Tags</h3>
                                                    <Form>
                                                        <Row className="g-2 mb-2">
                                                            <Col xs={6}>
                                                                <Form.Label className="small fw-semibold mb-1">Category</Form.Label>
                                                                <Typeahead size="sm" flip maxHeight="200px" placeholder="Film, Television…"
                                                                    selected={tagCategory ? [tagCategory] : []}
                                                                    options={tagFolders.find(f => f.name === 'Category')?.tags ?? []}
                                                                    onChange={(e) => { const v = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : ''; setTagCategory(v); setIsTV(v.toLowerCase().includes('television')); if (!v.toLowerCase().includes('television')) setTagIsPilot(false); }}
                                                                    onBlur={(e) => { const v = e.target.value; if (v && v !== tagCategory) { setTagCategory(v); setIsTV(v.toLowerCase().includes('television')); } }}
                                                                />
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Form.Label className="small fw-semibold mb-1">Industry List</Form.Label>
                                                                <Typeahead size="sm" flip maxHeight="200px" placeholder="WGA, DGA…"
                                                                    selected={tagIndustry ? [tagIndustry] : []}
                                                                    options={tagFolders.find(f => f.name === 'Industry List')?.tags ?? []}
                                                                    onChange={(e) => { const v = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : ''; setTagIndustry(v); }}
                                                                    onBlur={(e) => { const v = e.target.value; if (v && v !== tagIndustry) setTagIndustry(v); }}
                                                                />
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Form.Label className="small fw-semibold mb-1">Year</Form.Label>
                                                                <Typeahead size="sm" flip maxHeight="200px" placeholder="2024…"
                                                                    selected={tagYear ? [tagYear] : []}
                                                                    options={tagFolders.find(f => f.name === 'Year')?.tags ?? []}
                                                                    onChange={(e) => { const v = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : ''; setTagYear(v); }}
                                                                    onBlur={(e) => { const v = e.target.value; if (v && v !== tagYear) setTagYear(v); }}
                                                                />
                                                            </Col>
                                                            {(userData?.isApprover || (userData as any)?.isTagger || userData?.isIndexer) && (
                                                                <Col xs={6}>
                                                                    <Form.Label className="small fw-semibold mb-1">Genre</Form.Label>
                                                                    <Typeahead size="sm" flip maxHeight="200px" placeholder="Drama, Comedy…"
                                                                        multiple
                                                                        selected={tagGenre}
                                                                        options={tagFolders.find(f => f.name === 'Genre')?.tags ?? []}
                                                                        onChange={(e) => setTagGenre(e.map(v => typeof v === 'string' ? v : (v as any).label || ''))}
                                                                        renderToken={(option, { onRemove }, idx) => (
                                                                            <span key={idx} className="badge bg-secondary d-inline-flex align-items-center gap-1 me-1 mb-1 fw-normal">
                                                                                {option as string}
                                                                                <button type="button" onClick={() => onRemove(option)} aria-label="Remove" style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, color: 'inherit', opacity: 0.7, cursor: 'pointer', fontSize: '0.75rem' }}>×</button>
                                                                            </span>
                                                                        )}
                                                                    />
                                                                </Col>
                                                            )}
                                                            {isTV && (
                                                                <Col xs={12} className="d-flex align-items-center gap-3 pt-1">
                                                                    <span className="small fw-semibold">Pilot?</span>
                                                                    <Form.Check inline type="radio" id="sv_pilot_yes" label="Yes" checked={tagIsPilot} onChange={() => setTagIsPilot(true)} className="mb-0" />
                                                                    <Form.Check inline type="radio" id="sv_pilot_no" label="No" checked={!tagIsPilot} onChange={() => setTagIsPilot(false)} className="mb-0" />
                                                                </Col>
                                                            )}
                                                        </Row>
                                                        <Button size="sm" variant="primary" onClick={async () => {
                                                            const tagsToSave = [tagCategory, tagIndustry, tagYear, ...tagGenre, tagIsPilot ? 'Pilot' : ''].filter(Boolean);
                                                            const ok = await setScriptTags(file_id, tagsToSave);
                                                            if (ok !== false) {
                                                                setFileInfo(prev => ({ ...prev, tags: tagsToSave }));
                                                                showToast('success', 'Tags saved.');
                                                            }
                                                        }}>Save Tags</Button>
                                                    </Form>
                                                </>
                                            )}
                                        </Col>

                                        {isTV && fileInfo["Episode #"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Episode</h3>
                                                <p className="text-break">{fileInfo["Episode #"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Date Added"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Date Added</h3>
                                                <p className="text-break">{fileInfo["Date Added"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Draft Date"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Draft Date</h3>
                                                <p className="text-break">{fileInfo["Draft Date"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Document Type"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Document Type</h3>
                                                <p className="text-break">{fileInfo["Document Type"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Draft"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Version</h3>
                                                <p className="text-break">{fileInfo["Draft"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Alternate Titles"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Alternate Titles</h3>
                                                <p className="text-break">{fileInfo["Alternate Titles"]}</p>
                                            </Col>
                                        )}

                                        {fileInfo["Caveat"] && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Notes</h3>
                                                <p className="text-break">{fileInfo["Caveat"]}</p>
                                            </Col>
                                        )}

                                        {(fileInfo["Industry List"] || fileInfo["Year"]) && (
                                            <Col md={6} xs={12} className="min-zero">
                                                <h3 className="fs-6 fw-bold mt-2 mb-0">Industry Lists and Accolades</h3>
                                                <p className="text-break">{[fileInfo["Industry List"], fileInfo["Year"]].filter(Boolean).join(', ')}</p>
                                            </Col>
                                        )}
                                    </Row>
                                    {userData?.isApprover && (
                                        <Row className={isExtraLarge ? "mt-auto" : "mt-4"}>
                                            <Col xs={12}>
                                                <Row>
                                                    <h2 className="fs-6 fw-bold mb-3">Admin Information</h2>
                                                </Row>
                                                <Row>
                                                    <Col md={6} xs={12} className="min-zero">
                                                        <h3 className="fs-6 fw-bold mb-0">Last Updated</h3>
                                                        <p>{` ${formatDateTime(fileInfo["last_updated"])}`}</p>
                                                    </Col>
                                                    {modifiedBy && (
                                                        <Col md={6} xs={12} className="min-zero">
                                                            <h3 className="fs-6 fw-bold mb-0">Last Modified By</h3>
                                                            <p>{modifiedBy}</p>
                                                        </Col>
                                                    )}
                                                    {indexedBy && (
                                                        <Col md={6} xs={12} className="min-zero">
                                                            <h3 className="fs-6 fw-bold mb-0">Indexed By</h3>
                                                            <p>{indexedBy}</p>
                                                        </Col>
                                                    )}
                                                    {fileInfo["flag_reason"] && (
                                                        <Col md={6} xs={12} className="min-zero">
                                                            <h3 className="fs-6 fw-bold mb-0 text-danger">Flag Reason</h3>
                                                            <p className="text-danger">{fileInfo["flag_reason"]}</p>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Col>
                                        </Row>
                                    )}
                                    {userCanEdit && editorEmail && (<Row>
                                        <Col xs={12} className="mt-auto">
                                            <small role="alert">
                                                <strong>Note:</strong> Currently being edited by {editorEmail} as of {formatDateTime(fileInfo["user_editing_timestamp"])}.
                                            </small>
                                        </Col>
                                    </Row>)}
                                    <Stack direction="horizontal" gap={3} className={`${isExtraLarge ? "mt-auto" : "mt-3 mb-2 mb-md-5"} d-flex flex-wrap`}>
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                if (location.state?.from) {
                                                    navigate(-1); // go back
                                                } else {
                                                    navigate("/database"); // go to the database main page
                                                }
                                            }}
                                        >
                                            <i className="bi bi-arrow-left-circle me-2"></i>Back
                                        </Button>
                                        {userData?.isApprover && !recordDeleted && (
                                            <Button
                                                variant="outline-danger ms-auto"
                                                onClick={() => {
                                                    setShowDeleteModal(true);
                                                    setPendingAction(() => async () => {
                                                        setShowDeleteModal(false);
                                                        fetchData();
                                                    });
                                                }}
                                                disabled={checkedOut}
                                            >
                                                <i className="bi bi-trash3 me-2"></i>Delete
                                            </Button>
                                        )}
                                        {userData?.isApprover && fileInfo?.flag_reason && fileInfo?.flag_reason.toLowerCase().includes("duplicate") && !recordDeleted && (
                                            <Button
                                                variant="primary"
                                                onClick={() => {
                                                    setShowRemoveDuplicatesModal(true);
                                                    setPendingAction(() => async () => {
                                                        setShowRemoveDuplicatesModal(false);
                                                        showToast("success", "Duplicates successfully removed.", 2000);
                                                        fetchData();
                                                    });
                                                }}
                                            >
                                                <i className="bi bi-tools me-2" aria-label="Dedupe" title="Dedupe"></i>Dedupe
                                            </Button>
                                        )}
                                        {userData?.isApprover && recordDeleted && (
                                            <Button
                                                variant="outline-secondary ms-auto"
                                                onClick={() => {
                                                    setShowRestoreModal(true);
                                                    setPendingAction(() => async () => {
                                                        setShowRestoreModal(false);
                                                        fetchData();
                                                    });
                                                }}
                                                disabled={checkedOut}
                                            >
                                                <i className="bi bi-arrow-counterclockwise me-2"></i>Restore
                                            </Button>
                                        )}
                                        {userCanEdit && (
                                            <Button
                                                variant="primary ms-auto"
                                                onClick={() => navigate("edit", { state: { from: "script" } })}
                                                disabled={checkedOut}
                                            >
                                                <i className="bi bi-pencil-square me-2" aria-label="Edit" title="Edit"></i>Edit
                                            </Button>
                                        )}
                                        {editorEmail && (
                                            <Button
                                                variant="outline-danger"
                                                onClick={() => {
                                                    setConfirmationMessage("Are you sure you want to clear the current editor? Any pending changes will be lost.");
                                                    setShowConfirmationModal(true);
                                                    setPendingAction(() => async () => {
                                                        setShowConfirmationModal(false);
                                                        clearEditing(fileInfo["file_id"], fileInfo["user_editing_id"]).then((success) => {
                                                            if (success) {
                                                                showToast("info", "Editing status cleared.");
                                                            }
                                                            setCheckedOut(false);
                                                            setEditorEmail(null);
                                                        });
                                                    });
                                                }}
                                            >
                                                <i className="bi bi-exclamation-triangle me-2" aria-label="Edit" title="Edit"></i>Clear Editor
                                            </Button>
                                        )}
                                        <Button
                                            variant="primary"
                                            style={{
                                                cursor:
                                                    downloadingScriptId === fileInfo["file_id"]
                                                        ? "not-allowed"
                                                        : "pointer",
                                            }}
                                            disabled={downloadingScriptId === fileInfo["file_id"]}
                                            onClick={handleDownload}
                                        >
                                            {downloadingScriptId === fileInfo["file_id"] ? (
                                                <LoadingText text="Loading" className="mb-0" />
                                            ) : (
                                                <span><i className="bi bi-file-earmark-arrow-down-fill me-2" aria-label="Download" title="Download"></i>Download</span>
                                            )}
                                        </Button>
                                    </Stack>
                                </Col>
                                {(isBlacklisted || HIDE_PREVIEW) ?
                                    (
                                        <></>
                                    ) : (
                                        showPreview ?
                                        (
                                            <Col xl={6} xs={12} className="overflow-hidden h-100 d-flex flex-column min-zero order-first order-xl-1">
                                                <div className="border rounded overflow-hidden flex-grow-1 pdf-viewer">
                                                    {fileType === 'pdf' ? (
                                                        <PdfViewer
                                            fileUrl={url}
                                            fileName={fileInfo["Title"]}
                                            fileId={fileInfo["file_id"]}
                                            allowDownload
                                            onDownload={handleDownload}
                                            downloading={downloadingScriptId === fileInfo["file_id"]}
                                        />
                                                    ) : (
                                                        <AltViewer fileName={fileInfo["Title"]} />
                                                    )}
                                                </div>
                                            </Col>
                                        ) : (
                                            <Col xl={6} xs={12} className="overflow-hidden h-100 d-flex flex-column min-zero order-first order-xl-1">
                                                <div className="border rounded overflow-hidden flex-grow-1 d-flex flex-column align-items-center justify-content-center p-4">
                                                    <div className="text-center mb-4">
                                                        <i className="bi bi-shield-x text-muted" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3 mb-2">Preview Unavailable</h5>
                                                        {tooManyPreviews && (
                                                            <p className="text-muted">
                                                                You've previewed the maximum number of scripts allowed per day. Please try again tomorrow.
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        )
                                    )
                                }
                            </Row>
                        </Container>
                    ) : (
                        <LoadingRing />
                    )}
                </div>
            </div>
            <ConfirmationModal
                show={showConfirmationModal}
                message={confirmationMessage}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowConfirmationModal(false);
                    setPendingAction(null);
                }}
            />
            <DeleteConfirmationModal
                show={showDeleteModal}
                fileInfo={fileInfo}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowDeleteModal(false);
                    setPendingAction(null);
                }}
            />
            <RestoreConfirmationModal
                show={showRestoreModal}
                fileInfo={fileInfo}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowRestoreModal(false);
                    setPendingAction(null);
                }}
            />
            <RemoveDuplicatesModal
                show={showRemoveDuplicatesModal}
                fileInfo={fileInfo}
                onConfirm={pendingAction}
                onHide={() => {
                    setShowRemoveDuplicatesModal(false);
                    setPendingAction(null);
                }}
            />
        </div>
    );
};

export default ScriptView;